import '../upsert-collection-layer/upsert-collection-layer.js';
import '../wishlist-collection/wishlist-collection.js';
import { CSSResult, html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Messages, SharedCollection } from '../../../@types/wishlist.js';
import { repeat } from 'lit/directives/repeat.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import styles from './wishlist-curated-collections.scss?inline';
import { consume } from '@lit/context';
import {
  collectionsContext,
  curatedCollectionContext,
  productsContext,
} from '../../../scripts/context/wishlistContext.js';
import { CollectionsState, CuratedCollectionsState, ProductsState } from '../../../scripts/context/wishlist-state.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import {
  TrackedCuratedCollection,
  trackPageView,
  trackViewCollections,
  trackViewCuratedCollections,
  trackViewWishlist,
} from '../../../scripts/service/cube-tracking-layer.js';
import { curatorLayerContext, CuratorLayerState } from '../../../scripts/context/curatorLayerContext.js';
import { WishlistService } from '../../../scripts/service/wishlist-service.js';
import { vtoContext } from '../../../scripts/context/vtoContext.js';
import { VtoService } from '../../../scripts/service/vto-service.js';
import { saveCuratedListViewMode } from '../../../scripts/utils/view-mode-storage.js';
import { pushToSnackbar } from '../../../scripts/service/snackbar-service.js';
import type { VtoStatus } from '../../../scripts/service/vto-types.js';

@customElement('ents-wishlist-curated-collections')
export class WishlistCuratedCollections extends LitElement {
  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: curatedCollectionContext })
  private curatedCollectionsState!: CuratedCollectionsState;

  @consume({ context: collectionsContext })
  private collectionsState!: CollectionsState;

  @consume({ context: productsContext })
  private productsState!: ProductsState;

  @consume({ context: curatorLayerContext, subscribe: true })
  @property({ attribute: false })
  private curatorLayerState!: CuratorLayerState;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: vtoContext })
  private vtoService?: VtoService;

  @state()
  private collections: SharedCollection[] = [];

  @state() private hasProfileImage: boolean = false;
  @state() private vtoChecked: boolean = false;
  @state() private vtoStatus: VtoStatus = { kind: 'ok' };

  private trackingSent = false;

  private vtoStatusListener = (e: CustomEvent<VtoStatus>) => {
    this.vtoStatus = e.detail;
    this.showStatusSnackbar(e.detail);
  };

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  async loadCollections() {
    try {
      this.collections = await this.curatedCollectionsState.getAll(this.wishlistAppConfig.curatedCollectionApiEndpoint);
    } catch {
      this.collections = [];
    }
    this.trackPageEvents();
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadCollections();
    void this.checkVtoProfile();
    document.addEventListener('ents:vto:status', this.vtoStatusListener);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('ents:vto:status', this.vtoStatusListener);
  }

  private async checkVtoProfile(): Promise<void> {
    try {
      if (this.vtoService?.isAvailable() && this.wishlistAppConfig?.vtoEnabled) {
        const profile = await this.vtoService.getProfileImage();
        this.hasProfileImage = !!profile;
      }
    } catch (e) {
      console.error('[vto]', 'checkVtoProfile failed', e);
    } finally {
      this.vtoChecked = true;
    }
  }

  private trackPageEvents() {
    if (this.trackingSent) {
      return;
    }
    this.trackingSent = true;
    trackPageView('curated_collection_list', this.collections.length === 0, true);
    trackViewWishlist('curated_collection_list', this.productsState.entities);
    trackViewCollections('curated_collection_list', this.collectionsState.entities, this.productsState);

    const allCuratedCollections = this.curatedCollectionsState.entities;
    const curatedCollectionsToTrack: TrackedCuratedCollection[] = allCuratedCollections.map((entry) => ({
      collection: entry.collection,
      quantity: entry.products.length,
    }));
    trackViewCuratedCollections('curated_collection_list', curatedCollectionsToTrack);
  }

  private openCuratorDrawer() {
    const curator = this.collections[0]?.curator;
    if (curator) {
      this.curatorLayerState.update({ open: true, curator });
    }
  }

  private renderCurator() {
    const curator = this.collections[0]?.curator;

    if (!curator) return nothing;

    const subtitle = this.messages['ents_wishlist_curated_collections_curator_subtitle'] ?? '';
    const subtitleParts = subtitle.split('{}');

    return html`
      <div class="ents-wishlist-curator">
        <img src="${curator.image}" alt="${curator.name}" class="ents-wishlist-curator__image" />
        <h2 class="ents-heading">${this.messages['ents_wishlist_curated_collections_curator_title']}</h2>

        <p>${subtitleParts[0]}<strong>${curator.name}</strong>${subtitleParts[1] || ''}</p>

        <button
          class="bdk-button"
          type="button"
          variant="outline"
          size="small"
          @click="${this.openCuratorDrawer}"
          data-test-ents-curator-details-button
        >
          ${this.messages['ents_wishlist_curated_collection_hint_linktext']}
        </button>
      </div>
    `;
  }

  render() {
    const vtoUiHidden = this.vtoStatus.kind === 'vto-disabled' || this.vtoStatus.kind === 'session-suspended';
    const vtoCtaLabel = this.hasProfileImage
      ? this.messages['ents_wishlist_vto_cta_change']
      : this.messages['ents_wishlist_vto_cta_upload'];
    return html`
      <div class="ents-wishlist-collection-container">
        <h2 class="ents-heading-medium">${this.messages['ents_wishlist_curated_collections_title']}</h2>
        <p>${this.messages['ents_wishlist_curated_collections_subtitle']}</p>
        ${this.vtoChecked && this.wishlistAppConfig?.vtoEnabled && this.vtoService?.isAvailable() && !vtoUiHidden
          ? html`
              <div class="ents-vto-upload">
                <p>${this.messages['ents_wishlist_vto_description']}</p>
                <button
                  type="button"
                  class="bdk-button ents-vto-upload-cta"
                  variant="primary"
                  size="medium"
                  full-width
                  @click="${this.handleVtoUploadClick}"
                  data-test-ents-vto-upload-cta
                >
                  ${vtoCtaLabel}
                </button>
              </div>
            `
          : nothing}
        <div class="ents-wishlist-collection-grid">
          ${repeat(
            this.collections,
            (col) => col.collection.id,
            (col) => html`
              <article>
                <ents-wishlist-collection
                  .wishlistCollection="${col.collection}"
                  .products="${WishlistService.getProductsForCollectionWithDefaultSorting(
                    col.products,
                    col.collection.id,
                  )}"
                  curated-collection
                />
              </article>
            `,
          )}
        </div>
        ${this.renderCurator()}
      </div>
    `;
  }

  private handleVtoUploadClick = async (): Promise<void> => {
    if (!this.vtoService) return;
    if (this.vtoStatus.kind === 'cooldown') {
      void pushToSnackbar(this.messages['ents_wishlist_vto_snackbar_cooldown']!);
      return;
    }
    try {
      const profile = await this.vtoService.requestProfileImage();
      if (!profile) return;
      this.hasProfileImage = true;
      saveCuratedListViewMode('vto');
      const items = this.collectAllVtoItems();
      if (items.length > 0) await this.vtoService.prefetchVtos(items);
    } catch (e) {
      console.error('[vto]', 'handleVtoUploadClick failed', e);
      void pushToSnackbar(this.messages['ents_wishlist_vto_snackbar_generic_error']!);
    }
  };

  private collectAllVtoItems(): { syan: number; colorId: string }[] {
    return this.collections.flatMap((col) =>
      col.products.flatMap((p) => (p.colorId ? [{ syan: p.syan, colorId: p.colorId }] : [])),
    );
  }

  private showStatusSnackbar(status: VtoStatus): void {
    switch (status.kind) {
      case 'ok':
        return;
      case 'cooldown':
        void pushToSnackbar(this.messages['ents_wishlist_vto_snackbar_cooldown']!);
        return;
      case 'profile-invalid':
        void pushToSnackbar(
          this.messages['ents_wishlist_vto_snackbar_profile_invalid']!,
          undefined,
          this.messages['ents_wishlist_vto_snackbar_profile_invalid_action'],
          () => void this.handleVtoUploadClick(),
        );
        return;
      case 'vto-disabled':
        void pushToSnackbar(this.messages['ents_wishlist_vto_snackbar_disabled']!);
        return;
      case 'session-suspended':
        void pushToSnackbar(this.messages['ents_wishlist_vto_snackbar_session_suspended']!);
        return;
      default: {
        console.error('[vto]', 'unhandled VTO status', (status as VtoStatus).kind);
        return;
      }
    }
  }
}
